import { useState, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useQuery } from '@tanstack/react-query';
import type { Destination, ParkingLot } from '@arcnet/contracts';
import { useParkingLots, useSaveParkingLot, useDeleteParkingLot } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Toggle } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { api, ApiClientError } from '../../lib/api/client.js';

/**
 * Parking lots (docs/13 call_park).
 *
 * A park code and a run of slots. Transfer a call to the park code and it waits
 * in the next free slot, which is announced; dial the slot from any phone to
 * pick it up. A call nobody picks up rings whoever parked it, then goes to the
 * timeout destination.
 */

const slotRange = (start: number, count: number): string =>
  count === 1 ? String(start) : `${start}\u2013${start + count - 1}`;

const wait = (s: number): string => (s % 60 === 0 ? `${s / 60} min` : `${s}s`);

export function ParkingLotsTab({ tenantId }: { tenantId: string }) {
  // Creating, editing and deleting need queue:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = useSession((s) => s.can)('queue:write');
  const lots = useParkingLots(tenantId);
  const del = useDeleteParkingLot(tenantId);
  const [editing, setEditing] = useState<ParkingLot | null>(null);
  const [creating, setCreating] = useState(false);
  const list = lots.data?.data ?? [];

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Parking lots</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Park a call on a code, and pick it up from any phone by dialling its slot.
          </p>
        </div>
        <div className="arc-spacer" />
        {mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New lot'}
        </button>
        )}
      </div>

      {lots.error && <ErrorBanner error={lots.error} />}

      {(creating || editing) && (
        <LotForm key={editing?.id ?? 'new'} tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Lot</th><th>Park code</th><th>Slots</th><th>If nobody picks up</th><th />
              </tr>
            </thead>
            {lots.data === undefined ? <TableSkeleton cols={5} /> : (
              <tbody>
                {list.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 550 }}>{l.name}</td>
                    <td>
                      {l.extension
                        ? <span className="arc-ext-num">{l.extension}</span>
                        : <span className="arc-subtle">no code</span>}
                    </td>
                    <td className="arc-mono">{slotRange(l.slot_start, l.slot_count)}</td>
                    <td>
                      <span className="arc-subtle">After {wait(l.timeout_sec)}, the parker, then </span>
                      <DestinationLabel tenantId={tenantId} destination={l.timeout_destination} />
                    </td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayWrite && (
                        <button type="button"
                          className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-danger-ghost"
                          title={`Delete ${l.name}`} aria-label={`Delete ${l.name}`}
                          onClick={() => {
                            if (confirm(`Delete the "${l.name}" lot? Its park code and slots stop working.`)) {
                              del.mutate(l.id);
                            }
                          }}>
                          <Icon name="trash" size={15} />
                        </button>
                        )}
                        {mayWrite && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          title={`Edit ${l.name}`} aria-label={`Edit ${l.name}`}
                          onClick={() => { setEditing(l); setCreating(false); }}>
                          <Icon name="edit" size={15} />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {lots.data !== undefined && list.length === 0 && (
          <Empty icon="phone" title="No parking lots"
            hint="Create one with a park code and a run of slots." />
        )}
      </div>
    </>
  );
}

function LotForm({ tenantId, existing, onDone }: {
  tenantId: string; existing: ParkingLot | null; onDone: () => void;
}) {
  const save = useSaveParkingLot(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [extension, setExtension] = useState(existing?.extension ?? '');
  const [slotStart, setSlotStart] = useState(existing ? String(existing.slot_start) : '');
  const [slotCount, setSlotCount] = useState(String(existing?.slot_count ?? 10));
  const [timeoutSec, setTimeoutSec] = useState(String(existing?.timeout_sec ?? 120));
  const [destination, setDestination] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [announce, setAnnounce] = useState(existing?.announce_slot ?? true);
  const [moh, setMoh] = useState<string>(existing?.moh_media_id ?? '');

  // The same query the Media screens use, so the list is shared.
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{
      data: Array<{ id: string; kind: string; name: string }>; moh_default_id?: string | null;
    }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const mohFiles = (media.data?.data ?? []).filter((m) => m.kind === 'moh');
  const accountMoh = mohFiles.find((m) => m.id === media.data?.moh_default_id)?.name;

  const start = Number(slotStart);
  const count = Number(slotCount);
  const ready = Number.isInteger(start) && start >= 10 && Number.isInteger(count) && count >= 1;

  const err = save.error instanceof ApiClientError ? save.error : null;
  const fieldErr = err?.fieldErrors ?? {};

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name,
          extension: extension || null,
          slot_start: start,
          slot_count: count,
          timeout_sec: Number(timeoutSec),
          timeout_destination: destination,
          announce_slot: announce,
          moh_media_id: moh || null,
        }, { onSuccess: onDone });
      }}>
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New parking lot'}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 110px 110px 90px 110px', gap: 12, marginBottom: 6 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="pl-name">Name</label>
          <input id="pl-name" className="arc-input" value={name} required
            onChange={(e) => setName(e.target.value)} placeholder="Reception" />
          {fieldErr['name'] && <div className="arc-error">{fieldErr['name']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="pl-ext">Park code</label>
          <input id="pl-ext" className="arc-input arc-mono" value={extension} inputMode="numeric"
            onChange={(e) => setExtension(e.target.value)} placeholder="700" />
          {fieldErr['extension'] && <div className="arc-error">{fieldErr['extension']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="pl-start">First slot</label>
          <input id="pl-start" className="arc-input arc-mono" value={slotStart} inputMode="numeric" required
            onChange={(e) => setSlotStart(e.target.value)} placeholder="701" />
          {fieldErr['slot_start'] && <div className="arc-error">{fieldErr['slot_start']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="pl-count">Slots</label>
          <input id="pl-count" className="arc-input" value={slotCount} inputMode="numeric"
            onChange={(e) => setSlotCount(e.target.value)} />
          {fieldErr['slot_count'] && <div className="arc-error">{fieldErr['slot_count']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="pl-wait">Wait (s)</label>
          <input id="pl-wait" className="arc-input" value={timeoutSec} inputMode="numeric"
            onChange={(e) => setTimeoutSec(e.target.value)} />
          {fieldErr['timeout_sec'] && <div className="arc-error">{fieldErr['timeout_sec']}</div>}
        </div>
      </div>
      <p className="arc-hint" style={{ marginBottom: 14 }}>
        {ready
          ? `Transfer a call to ${extension || 'the park code'} to park it in ${slotRange(start, count)}. Dial the slot to pick it up.`
          : 'Choose the first slot and how many.'}
      </p>

      <div className="arc-field">
        <label className="arc-label">If nobody picks it up</label>
        <DestinationPicker tenantId={tenantId} value={destination} onChange={setDestination} allowNone />
        <span className="arc-hint">It rings whoever parked it first. With nothing chosen, the call ends.</span>
      </div>

      <div className="arc-field" style={{ maxWidth: 420 }}>
        <label className="arc-label" htmlFor="pl-moh">Music while parked</label>
        <select id="pl-moh" className="arc-select" value={moh} onChange={(e) => setMoh(e.target.value)}>
          <option value="">{accountMoh ? `Account hold music (${accountMoh})` : 'Account hold music (platform default)'}</option>
          {mohFiles.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Toggle id="pl-announce" label="Announce the slot to whoever parks the call"
          checked={announce} onChange={setAnnounce} />
      </div>

      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving\u2026' : existing ? 'Save changes' : 'Create lot'}
        </button>
        <button type="button" className="arc-btn arc-btn-ghost" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
